import type { StatusPresentation } from "../statusPresentation";
import type {
  Criterion,
  CriterionProgress,
  QualifyingWindow,
  TierStatus,
  VolumeConverterData,
  VolumeUnit,
} from "../types";
import type { RebateTierTranslatedContent } from "../useRebateTierTranslatedContent";

import { ChevronUpDoubleIcon, ClockIcon } from "../../Icons";
import { PopoverMenu } from "../../PopoverMenu";
import { TextButton } from "../../TextButton";
import { Typo } from "../../Typo";
import { CycleCard } from "../modals/CycleCard";
import { CriterionBar } from "./CriterionBar";

type CriteriaProgressProps = {
  criteria: CriterionProgress[];
  heading: string;
  view: StatusPresentation["criteriaView"];
  showUpgradeAffordance: boolean;
  daysRemaining: number | null;
  isAtRisk: boolean;
  cycleEndsAt: string | null;
  qualifyingWindow: QualifyingWindow | null;
  content: RebateTierTranslatedContent;
  volumeUnit: VolumeUnit;
  status: TierStatus;
  lastUpdated: string | null;
  onOpenCriteria: (criterion: Criterion) => void;
  converter: VolumeConverterData;
  onApplyVolumeUnit: (unit: VolumeUnit, saveAsDefault: boolean) => void;
  isMobile: boolean;
  explainedCriterion: Criterion | null;
  onOpenConverter: () => void;
  isConverterOpen: boolean;
  onOpenCycle: () => void;
  isCycleOpen: boolean;
  onOpenCycleCriteria: () => void;
};

export const CriteriaProgress = ({
  criteria,
  heading,
  view,
  showUpgradeAffordance,
  daysRemaining,
  isAtRisk,
  cycleEndsAt,
  qualifyingWindow,
  content,
  volumeUnit,
  status,
  lastUpdated,
  onOpenCriteria,
  converter,
  onApplyVolumeUnit,
  isMobile,
  explainedCriterion,
  onOpenConverter,
  isConverterOpen,
  onOpenCycle,
  isCycleOpen,
  onOpenCycleCriteria,
}: CriteriaProgressProps) => {
  const unitLabel =
    volumeUnit.kind === "usd"
      ? content.millionUsd
      : content.lotsOfSymbol(volumeUnit.symbol);

  const criterionCopy: Record<
    Criterion,
    { label: string; unitLabel?: string }
  > = {
    tv: {
      label: content.tradingVolume,
      unitLabel,
    },
    ac: {
      label: content.activeClients,
    },
    nc: {
      label: content.newClients,
    },
  };

  const clockClassName = isAtRisk
    ? "h-3 w-3 shrink-0 lg:h-4 lg:w-4 [&_path]:stroke-icon-warning-default"
    : "h-3 w-3 shrink-0 lg:h-4 lg:w-4 [&_path]:stroke-icon-brand-default";
  const mobileCountdownColor = isAtRisk
    ? "text-text-warning-default"
    : "text-text-brand-default";

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Typo
            element="h3"
            smType="labelSM"
            lgType="labelMD"
            textColor="text-text-primary"
          >
            {heading}
          </Typo>
          {showUpgradeAffordance && (
            <ChevronUpDoubleIcon height={20} width={20} aria-hidden="true" />
          )}
        </div>

        {view === "progress" &&
          daysRemaining !== null &&
          (isMobile ? (
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={isCycleOpen}
              onClick={onOpenCycle}
              className="flex shrink-0 items-center gap-2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused"
            >
              <ClockIcon aria-hidden="true" className={clockClassName} />
              <Typo
                element="span"
                type="labelXS"
                textColor={mobileCountdownColor}
                className="underline"
              >
                {content.daysRemaining(daysRemaining)}
              </Typo>
            </button>
          ) : (
            <PopoverMenu
              place="bottom"
              contentClassName="w-96 max-w-[calc(100vw-32px)] p-6"
              content={(close) => (
                <CycleCard
                  status={status}
                  cycleEndsAt={cycleEndsAt}
                  qualifyingWindow={qualifyingWindow}
                  content={content}
                  onOpenCriteria={() => {
                    close();
                    onOpenCycleCriteria();
                  }}
                />
              )}
              asChild
            >
              <TextButton
                size={{ base: "sm", lg: "md" }}
                iconSize={{ base: "xs", lg: "sm" }}
                tone={isAtRisk ? "warning" : "link"}
                focusStyle="outline"
                className="shrink-0"
                iconL={
                  <ClockIcon aria-hidden="true" className={clockClassName} />
                }
                data-testid="rebate_tier_qualification_countdown_button"
              >
                {content.daysRemaining(daysRemaining)}
              </TextButton>
            </PopoverMenu>
          ))}
      </div>

      {criteria.map((progress) => {
        const copy = criterionCopy[progress.criterion];
        return (
          <CriterionBar
            key={progress.criterion}
            progress={progress}
            view={view}
            label={copy.label}
            explainLabel={content.explainCriterion(copy.label)}
            unitLabel={copy.unitLabel}
            volumeUnit={progress.criterion === "tv" ? volumeUnit : undefined}
            status={status}
            isAtRisk={isAtRisk}
            lastUpdated={lastUpdated}
            content={content}
            isMobile={isMobile}
            onExplain={() => onOpenCriteria(progress.criterion)}
            isExplainerOpen={explainedCriterion === progress.criterion}
            converter={copy.unitLabel ? converter : undefined}
            onApplyVolumeUnit={copy.unitLabel ? onApplyVolumeUnit : undefined}
            onOpenConverter={copy.unitLabel ? onOpenConverter : undefined}
            isConverterOpen={copy.unitLabel ? isConverterOpen : undefined}
          />
        );
      })}
    </div>
  );
};
